import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { useLang } from '../i18n/LanguageContext';
import { reservationCheckinFormText, ReservationCheckinFormText } from '../i18n/pages/reservationCheckinForm';
import {
  getManagerCheckinForm,
  updateManagerMemberSlot,
  resetManagerMemberSlot,
  resetAllGuests,
  submitAllToUbyport,
  resendCheckinEmail,
  ManagerCheckinForm,
  ManagerMemberSlot,
  UpdateMemberSlotRequest,
  UbyportMappedOption,
  Reservation,
} from '../api.reservations';
import { DorucenkaDocument, getDorucenky, downloadDorucenka, downloadAllDorucenky } from '../api.dorucenka';

const getToken = () => localStorage.getItem('token') || '';

/** ISO 3166-1 alpha-3 code for Czech Republic. Czech citizens are exempt from Ubyport registration. */
const CZECH_REPUBLIC_CODE = 'CZE';

/** documentType values that never require a visa (green passport, with or without residence permit) */
const DOC_TYPE_GREEN_PASSPORT = 'green_passport';
const DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE = 'green_passport_eu_residence';
const isVisaExemptDocType = (documentType: string | undefined): boolean =>
  documentType === DOC_TYPE_GREEN_PASSPORT || documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Formats a Date as "YYYY-MM-DD" using LOCAL date parts (toISOString() would shift to UTC). */
const toLocalIsoDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** A guest can't have been born today or later — the last valid date of birth is yesterday. */
const maxDateOfBirth = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d;
};

/** Resolve a stored submit value to its display label (EN preferred, CZ fallback, code last resort). */
const resolveLabel = (code: string | undefined, options: UbyportMappedOption[]): string => {
  if (!code) return '—';
  const opt = options.find(o => o.submitValue === code);
  if (!opt) return code;
  return opt.displayEN || opt.displayCZ || code;
};

// ─── Signature rendering ─────────────────────────────────────────────────────

const SIG_PADDING = 12;

function computeViewBox(d: string): string {
  const nums = d.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 2) return '0 0 360 230';
  const xs: number[] = [], ys: number[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    xs.push(parseFloat(nums[i]));
    ys.push(parseFloat(nums[i + 1]));
  }
  const minX = Math.min(...xs) - SIG_PADDING;
  const minY = Math.min(...ys) - SIG_PADDING;
  const w = Math.max(...xs) - Math.min(...xs) + SIG_PADDING * 2;
  const h = Math.max(...ys) - Math.min(...ys) + SIG_PADDING * 2;
  return `${minX} ${minY} ${w} ${h}`;
}

const SignatureDisplay: React.FC<{ svg?: string; noSigText: string }> = ({ svg, noSigText }) => {
  if (!svg || !svg.trim()) {
    return <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>{noSigText}</span>;
  }
  return (
    <svg
      viewBox={computeViewBox(svg)}
      preserveAspectRatio="xMidYMid meet"
      style={{
        width: '100%',
        maxWidth: 360,
        height: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        background: '#f8fafc',
        display: 'block',
      }}
    >
      <path d={svg} fill="#1a202c" />
    </svg>
  );
};

// ─── Field label helper ───────────────────────────────────────────────────────

const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </span>
    <div style={{ color: value ? '#1a202c' : '#a0aec0', fontStyle: value ? 'normal' : 'italic', marginTop: 2 }}>
      {value || '—'}
    </div>
  </div>
);

// ─── Edit form ────────────────────────────────────────────────────────────────

interface EditFormProps {
  slot: ManagerMemberSlot;
  states: UbyportMappedOption[];
  purposes: UbyportMappedOption[];
  onSave: (data: UpdateMemberSlotRequest) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  isMobile?: boolean;
  t: ReservationCheckinFormText;
}

const EditForm: React.FC<EditFormProps> = ({ slot, states, purposes, onSave, onCancel, saving, isMobile, t }) => {
  const [draft, setDraft] = useState<UpdateMemberSlotRequest>({
    firstName: slot.firstName || '',
    surname: slot.surname || '',
    dateOfBirth: slot.dateOfBirth || '',
    phone: slot.phone || '',
    address: slot.address || '',
    country: slot.country || '',
    purposeOfStay: slot.purposeOfStay || '',
    documentType: slot.documentType || '',
    documentNumber: slot.documentNumber || '',
    documentCountry: slot.documentCountry || '',
    visaNumber: slot.visaNumber || '',
    residencePermitNumber: slot.residencePermitNumber || '',
    hasDisabilityCard: slot.hasDisabilityCard || false,
  });

  const setField = (field: keyof UpdateMemberSlotRequest) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setDraft(prev => ({ ...prev, [field]: e.target.value }));

  // `max` on a date input is advisory — most browsers still accept a typed or pasted value, and
  // some mobile keyboards ignore it outright. The backend rejects an impossible date too; this is
  // so the manager sees why before losing the rest of what they typed.
  // Both sides are "YYYY-MM-DD", so a string compare is the date compare — no timezone to get
  // wrong by parsing an ISO date into a Date and reading it back in local time.
  const dateOfBirthInvalid = !!draft.dateOfBirth
    && draft.dateOfBirth > toLocalIsoDate(maxDateOfBirth());

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: isMobile ? '5px 8px' : '6px 10px', border: '1px solid #cbd5e0', borderRadius: 6,
    fontSize: isMobile ? '0.9rem' : '0.95rem', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontWeight: 600, fontSize: isMobile ? '0.72rem' : '0.8rem', color: '#718096',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: isMobile ? 2 : 4,
  };
  const fieldWrap: React.CSSProperties = { marginBottom: isMobile ? 8 : 14 };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: isMobile ? 6 : 12 }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelFirstName}</label>
          <input style={inputStyle} value={draft.firstName || ''} onChange={setField('firstName')} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelSurname}</label>
          <input style={inputStyle} value={draft.surname || ''} onChange={setField('surname')} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelDateOfBirth}</label>
          <input style={dateOfBirthInvalid ? { ...inputStyle, borderColor: '#fc8181' } : inputStyle} type="date"
            max={toLocalIsoDate(maxDateOfBirth())}
            value={draft.dateOfBirth || ''}
            onChange={e => setDraft(prev => ({ ...prev, dateOfBirth: e.target.value || undefined }))} />
          {dateOfBirthInvalid && (
            <div style={{ color: '#c53030', fontSize: '0.78rem', marginTop: 3 }}>{t.dateOfBirthInvalid}</div>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelPhone}</label>
          <input style={inputStyle} value={draft.phone || ''} onChange={setField('phone')} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelAddress}</label>
          <input style={inputStyle} value={draft.address || ''} onChange={setField('address')} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelCountry}</label>
          {states.length > 0 ? (
            <select style={inputStyle} value={draft.country || ''} onChange={setField('country')}>
              <option value="">{t.selectCountry}</option>
              <option value={CZECH_REPUBLIC_CODE}>Czech Republic / Česká republika</option>
              {states.map((s, i) => (
                <option key={`state-${s.submitValue}-${i}`} value={s.submitValue}>
                  {s.displayEN || s.displayCZ || s.submitValue}
                </option>
              ))}
            </select>
          ) : (
            <input style={inputStyle} value={draft.country || ''} onChange={setField('country')} placeholder="Country code" />
          )}
        </div>
        {draft.country === CZECH_REPUBLIC_CODE && (
          <div style={{ ...fieldWrap, gridColumn: '1 / -1', padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.85rem', color: '#1e40af' }}>
            {t.czechCitizenNote}
          </div>
        )}
        {draft.country !== CZECH_REPUBLIC_CODE && (
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelPurpose}</label>
          {purposes.length > 0 ? (
            <select style={inputStyle} value={draft.purposeOfStay || ''} onChange={setField('purposeOfStay')}>
              <option value="">{t.selectPurpose}</option>
              {purposes.map((p, i) => (
                <option key={`purpose-${p.submitValue}-${i}`} value={p.submitValue}>
                  {p.displayEN || p.displayCZ || p.submitValue}
                </option>
              ))}
            </select>
          ) : (
            <input style={inputStyle} value={draft.purposeOfStay || ''} onChange={setField('purposeOfStay')} />
          )}
        </div>
        )}
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelDocType}</label>
          <select style={inputStyle} value={draft.documentType || ''} onChange={setField('documentType')}>
            <option value="">{t.selectDocType}</option>
            <option value="passport">{t.docPassport}</option>
            <option value="id_card">{t.docIdCard}</option>
            <option value="driver_license">{t.docDriverLicense}</option>
            <option value={DOC_TYPE_GREEN_PASSPORT}>{t.docGreenPassport}</option>
            <option value={DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE}>{t.docGreenPassportEuResidence}</option>
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelDocNumber}</label>
          <input style={inputStyle} value={draft.documentNumber || ''} onChange={setField('documentNumber')} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>{t.labelDocCountry}</label>
          {states.length > 0 ? (
            <select style={inputStyle} value={draft.documentCountry || ''} onChange={setField('documentCountry')}>
              <option value="">{t.selectCountry}</option>
              <option value={CZECH_REPUBLIC_CODE}>Czech Republic / Česká republika</option>
              {states.map((s, i) => (
                <option key={`dc-${s.submitValue}-${i}`} value={s.submitValue}>
                  {s.displayEN || s.displayCZ || s.submitValue}
                </option>
              ))}
            </select>
          ) : (
            <input style={inputStyle} value={draft.documentCountry || ''} onChange={setField('documentCountry')} placeholder="Country code" />
          )}
        </div>
        {!isVisaExemptDocType(draft.documentType) && (
          <div style={fieldWrap}>
            <label style={labelStyle}>{t.labelVisaNumber}</label>
            <input style={inputStyle} value={draft.visaNumber || ''} onChange={setField('visaNumber')} />
          </div>
        )}
        {draft.documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE && (
          <div style={fieldWrap}>
            <label style={labelStyle}>{t.labelResidencePermitNumber}</label>
            <input style={inputStyle} value={draft.residencePermitNumber || ''} onChange={setField('residencePermitNumber')} maxLength={15} />
          </div>
        )}
        <div style={{ ...fieldWrap, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="hasDisabilityCard"
            checked={!!draft.hasDisabilityCard}
            onChange={e => setDraft(prev => ({ ...prev, hasDisabilityCard: e.target.checked }))}
            style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
          />
          <label htmlFor="hasDisabilityCard" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', fontSize: '0.9rem', fontWeight: 600, color: '#2d3748' }}>
            {t.disabilityCard}
          </label>
        </div>
      </div>

      {draft.country !== CZECH_REPUBLIC_CODE && (
      <div style={{ marginTop: 4, padding: '10px 14px', background: '#f7f8fa', borderRadius: 6, border: '1px solid #e2e8f0', marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#718096', marginBottom: 8 }}>{t.signatureReadOnly}</div>
        <SignatureDisplay svg={slot.signatureSvg} noSigText={t.noSignature} />
        <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#a0aec0' }}>
          {t.signatureNote}
        </div>
      </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => onSave(draft)}
          disabled={saving || dateOfBirthInvalid}
          style={{ padding: '8px 20px', background: saving || dateOfBirthInvalid ? '#a0aec0' : '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: saving || dateOfBirthInvalid ? 'not-allowed' : 'pointer', fontWeight: 600 }}
        >
          {saving ? t.savingBtn : t.saveBtn}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{ padding: '8px 20px', background: '#edf2f7', color: '#2d3748', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          {t.cancelBtn}
        </button>
      </div>
    </div>
  );
};

// ─── Slot card ────────────────────────────────────────────────────────────────

/**
 * A complete foreign guest nobody has tried to file yet. Derived, never stored: after the auto-send
 * cap (#206) these are exactly the guests the manager still has to send. A rejected guest has an
 * error and is a failure, not "awaiting".
 */
export const isAwaitingManualSend = (m: ManagerMemberSlot): boolean =>
  !!m.isComplete && m.country !== CZECH_REPUBLIC_CODE && !m.ubyportSentAt && !m.ubyportError;

interface SlotCardProps {
  slot: ManagerMemberSlot;
  /** Another guest of this reservation is already filed - what makes "waiting" mean "held back". */
  otherGuestFiled?: boolean;
  states: UbyportMappedOption[];
  purposes: UbyportMappedOption[];
  onEdit: () => void;
  onReset: () => void;
  onSave: (data: UpdateMemberSlotRequest) => Promise<void>;
  onCancelEdit: () => void;
  isEditing: boolean;
  saving: boolean;
  isMobile?: boolean;
  t: ReservationCheckinFormText;
}

const SlotCard: React.FC<SlotCardProps> = ({ slot, otherGuestFiled, states, purposes, onEdit, onReset, onSave, onCancelEdit, isEditing, saving, isMobile, t }) => {
  const badgeStyle = (complete: boolean): React.CSSProperties => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700,
    background: complete ? '#c6f6d5' : '#fed7d7', color: complete ? '#276749' : '#9b2c2c',
  });

  const hasData = slot.isComplete || !!slot.firstName || !!slot.surname;

  const cardPad = isMobile ? '8px 12px' : '12px 16px';
  const bodyPad = isMobile ? 10 : 16;

  const resolveDocType = (value: string | undefined): string => {
    if (!value) return '—';
    if (value === 'passport') return t.docPassport;
    if (value === 'id_card') return t.docIdCard;
    if (value === 'driver_license') return t.docDriverLicense;
    if (value === DOC_TYPE_GREEN_PASSPORT) return t.docGreenPassport;
    if (value === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE) return t.docGreenPassportEuResidence;
    return value;
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: isMobile ? 10 : 18, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: cardPad, background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
        <span style={{ fontWeight: 700, fontSize: isMobile ? '0.88rem' : '1rem' }}>{t.guestN}{slot.slotIndex + 1}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {slot.country === CZECH_REPUBLIC_CODE && (
            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#eff6ff', color: '#1e40af' }}>
              {t.czCitizenBadge}
            </span>
          )}
          <span style={badgeStyle(slot.isComplete)}>{slot.isComplete ? t.slotComplete : t.slotEmpty}</span>
          {slot.isComplete && slot.country !== CZECH_REPUBLIC_CODE && (
            slot.ubyportSentAt
              ? <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#c6f6d5', color: '#22543d' }}>{t.slotSentBadge}</span>
              : slot.ubyportError
                ? <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: '#fed7d7', color: '#742a2a' }} title={slot.ubyportError}>{t.slotFailedBadge}</span>
                : <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: otherGuestFiled && isAwaitingManualSend(slot) ? '#ebf8ff' : '#e2e8f0', color: otherGuestFiled && isAwaitingManualSend(slot) ? '#2c5282' : '#4a5568' }}>{otherGuestFiled && isAwaitingManualSend(slot) ? t.slotAwaitingManualBadge : t.slotPendingBadge}</span>
          )}
        </div>
      </div>
      <div style={{ padding: bodyPad }}>
        {isEditing ? (
          <EditForm slot={slot} states={states} purposes={purposes} onSave={onSave} onCancel={onCancelEdit} saving={saving} isMobile={isMobile} t={t} />
        ) : hasData ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 5 : 8, marginBottom: isMobile ? 10 : 16 }}>
              <Field label={t.fieldFirstName} value={slot.firstName} />
              <Field label={t.fieldSurname} value={slot.surname} />
              {slot.dateOfBirth && <Field label={t.fieldDateOfBirth} value={slot.dateOfBirth} />}
              <Field label={t.fieldPhone} value={slot.phone} />
              <Field label={t.fieldAddress} value={slot.address} />
              <Field label={t.fieldCountry} value={resolveLabel(slot.country, states)} />
              <Field label={t.fieldPurpose} value={resolveLabel(slot.purposeOfStay, purposes)} />
              <Field label={t.fieldDocType} value={resolveDocType(slot.documentType)} />
              <Field label={t.fieldDocNumber} value={slot.documentNumber} />
              <Field label={t.fieldDocCountry} value={resolveLabel(slot.documentCountry, states)} />
              {!isVisaExemptDocType(slot.documentType) && <Field label={t.fieldVisaNumber} value={slot.visaNumber} />}
              {slot.documentType === DOC_TYPE_GREEN_PASSPORT_EU_RESIDENCE && (
                <Field label={t.fieldResidencePermitNumber} value={slot.residencePermitNumber} />
              )}
            </div>
            <div style={{ marginBottom: isMobile ? 10 : 16 }}>
              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {t.signatureLabel}
              </div>
              <SignatureDisplay svg={slot.signatureSvg} noSigText={t.noSignature} />
            </div>
            {slot.ubyportError && !slot.ubyportSentAt && (
              <div style={{ marginBottom: 12, fontSize: '0.82rem', color: '#c53030', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 6, padding: '6px 10px' }}>
                {t.ubyportErrorPrefix}{slot.ubyportError}
              </div>
            )}
            {slot.ubyportError && slot.ubyportSentAt && (
              <div style={{ marginBottom: 12, fontSize: '0.82rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px' }}>
                {t.ubyportNotePrefix}{slot.ubyportError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={onEdit}
                style={{ padding: isMobile ? '6px 14px' : '7px 18px', background: '#edf2f7', color: '#2d3748', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: isMobile ? '0.85rem' : undefined }}
              >
                {t.editBtn}
              </button>
              <button
                onClick={onReset}
                style={{ padding: isMobile ? '6px 14px' : '7px 18px', background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: isMobile ? '0.85rem' : undefined }}
              >
                {t.resetSlotBtn}
              </button>
            </div>
          </>
        ) : (
          <div style={{ color: '#a0aec0', fontStyle: 'italic', padding: '8px 0' }}>{t.noDataYet}</div>
        )}
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const getUbyportStatusBadge = (status: string | undefined, t: ReservationCheckinFormText, hasError = true) => {
  switch (status) {
    case 'SENT':             return { bg: '#c6f6d5', color: '#22543d', label: t.ubyStatusSent };
    case 'SENDING':          return { bg: '#feebc8', color: '#7b341e', label: t.ubyStatusSending };
    // PARTIAL with no error text: nothing failed, the auto-send cap left guests for the manager (#206).
    case 'PARTIAL':          return hasError
      ? { bg: '#fed7d7', color: '#742a2a', label: t.ubyStatusPartial }
      : { bg: '#ebf8ff', color: '#2c5282', label: t.ubyStatusPartialAwaiting };
    case 'SEND_FAILED':      return { bg: '#fed7d7', color: '#742a2a', label: t.ubyStatusFailed };
    case 'SEND_UNAVAILABLE': return { bg: '#feebc8', color: '#7b341e', label: t.ubyStatusUnavailable };
    default:                 return { bg: '#e2e8f0', color: '#2d3748', label: t.ubyStatusNotSent };
  }
};

const ReservationCheckinForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reservationId = Number(id);
  const { lang } = useLang();
  const t = reservationCheckinFormText[lang];

  const [form, setForm] = useState<ManagerCheckinForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [editingSlot, setEditingSlot] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [submittingAll, setSubmittingAll] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resetResult, setResetResult] = useState<Reservation | null>(null);
  const [dorucenky, setDorucenky] = useState<DorucenkaDocument[]>([]);
  const isMobile = useIsMobile();

  const loadDorucenky = useCallback(async () => {
    try {
      const docs = await getDorucenky(reservationId);
      setDorucenky(docs);
    } catch {
      // Non-fatal — Dorucenky may not be available (S3 disabled or none issued yet)
    }
  }, [reservationId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getManagerCheckinForm(reservationId, getToken());
      setForm(data);
    } catch {
      setError(t.errorLoadForm);
    } finally {
      setLoading(false);
    }
  }, [reservationId, t.errorLoadForm]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDorucenky(); }, [loadDorucenky]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3500);
  };

  const handleSave = async (slotIndex: number, data: UpdateMemberSlotRequest) => {
    setSaving(true);
    setError(null);
    try {
      await updateManagerMemberSlot(reservationId, slotIndex, data, getToken());
      setEditingSlot(null);
      await load();
      showSuccess(t.successSaveSlot);
    } catch {
      setError(t.errorSaveSlot);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (slotIndex: number, displayName?: string) => {
    const label = displayName ? `"${displayName}"` : `slot ${slotIndex + 1}`;
    if (!window.confirm(`${t.confirmResetSlotPre}${label}${t.confirmResetSlotPost}`)) return;
    setError(null);
    try {
      await resetManagerMemberSlot(reservationId, slotIndex, getToken());
      await load();
      showSuccess(t.successResetSlot);
    } catch {
      setError(t.errorResetSlot);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm(t.confirmResetAll)) return;
    setResettingAll(true);
    setError(null);
    setResetResult(null);
    try {
      const updated = await resetAllGuests(reservationId, getToken());
      setResetResult(updated);
      await load();
      showSuccess(t.successResetAll);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorSubmitAll);
    } finally {
      setResettingAll(false);
    }
  };

  const handleResendEmail = async () => {
    if (!form?.guestEmail) return;
    setResendingEmail(true);
    setError(null);
    try {
      await resendCheckinEmail(reservationId, getToken());
      showSuccess(`${t.successResendEmailPre}${form.guestEmail}.`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorResendEmail);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmitAll = async () => {
    if (!window.confirm(t.confirmSubmitAll)) return;
    setSubmittingAll(true);
    setError(null);
    try {
      await submitAllToUbyport(reservationId, getToken());
      await Promise.all([load(), loadDorucenky()]);
      showSuccess(t.successSubmitAll);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorSubmitAll);
    } finally {
      setSubmittingAll(false);
    }
  };

  const states = form?.states ?? [];
  const purposes = form?.purposes ?? [];

  const completedBadge: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700,
    background: '#c6f6d5', color: '#276749',
  };
  const incompleteBadge: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700,
    background: '#fed7d7', color: '#9b2c2c',
  };

  const allSlotsComplete = (form?.members.length ?? 0) > 0 && form?.members.every(m => m.isComplete);
  const todayStr = new Date().toISOString().slice(0, 10);
  const isFutureCheckin = !!form?.checkInDate && form.checkInDate > todayStr;
  // A SENT stay can still have a foreign guest nobody filed: a slot added or completed after the
  // others were sent, too late for auto-send. The status does not move for that, so ask the guests.
  const unsentForeignCount = form
    ? form.members.filter(m => m.isComplete && m.country !== CZECH_REPUBLIC_CODE && !m.ubyportSentAt).length
    : 0;
  const sentWithUnsentGuests = form?.ubyportStatus === 'SENT' && unsentForeignCount > 0;
  const canSubmit = allSlotsComplete && !isFutureCheckin
    && (form?.ubyportStatus !== 'SENT' || sentWithUnsentGuests)
    && form?.ubyportStatus !== 'SENDING';
  const ubyStatus = getUbyportStatusBadge(form?.ubyportStatus, t, !!form?.ubyportError);
  const awaitingManualCount = form ? form.members.filter(isAwaitingManualSend).length : 0;
  const anyGuestFiled = !!form?.members.some(m => !!m.ubyportSentAt);

  const btnPad = isMobile ? '6px 14px' : '8px 20px';
  const btnFs = isMobile ? '0.85rem' : '0.95rem';

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '12px 10px' : '24px 16px' }}>
      <div style={{ marginBottom: isMobile ? 12 : 20 }}>
        <Link to="/reservations" style={{ color: '#3182ce', textDecoration: 'none', fontWeight: 600, fontSize: isMobile ? '0.82rem' : '0.9rem' }}>
          {t.backToReservations}
        </Link>
      </div>

      {loading && <div style={{ padding: 32, textAlign: 'center', color: '#718096' }}>{t.loading}</div>}

      {error && (
        <div style={{ padding: '12px 16px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, color: '#c53030', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{ padding: '12px 16px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, color: '#276749', marginBottom: 16 }}>
          {successMsg}
        </div>
      )}

      {!loading && form && (
        <>
          <div style={{ marginBottom: isMobile ? 14 : 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 700 }}>
                {t.guestFormTitle}{form.apartmentName}
              </h1>
              <span style={form.isCompleted ? completedBadge : incompleteBadge}>
                {form.isCompleted ? t.formCompleted : t.formIncomplete}
              </span>
              <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700, background: ubyStatus.bg, color: ubyStatus.color }}>
                {ubyStatus.label}
              </span>
            </div>
            <div style={{ marginTop: isMobile ? 4 : 6, color: '#718096', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
              {form.checkInDate} → {form.checkOutDate} · {form.numberOfGuests != null ? `${form.numberOfGuests} ${t.guestsLabel}` : `? ${t.guestsLabel}`}
            </div>
            <div style={{ marginTop: 4, fontSize: isMobile ? '0.8rem' : '0.875rem', display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'center' }}>
              {form.guestEmail ? (
                <a href={`mailto:${form.guestEmail}`} style={{ color: '#3182ce', textDecoration: 'none' }}>
                  ✉ {form.guestEmail}
                </a>
              ) : (
                <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>{t.noEmail}</span>
              )}
              {form.formCreatedAt && (
                <span style={{ color: '#718096' }}>
                  {t.formStarted}{new Date(form.formCreatedAt).toLocaleString()}
                </span>
              )}
            </div>
            {form.ubyportSubmittedBy && (
              <div style={{ marginTop: 4, fontSize: '0.8rem', color: '#718096' }}>
                {form.ubyportSubmittedBy === 'MANUAL' && t.submittedManual}
                {form.ubyportSubmittedBy === 'AUTO_TRIGGER' && t.submittedAutoTrigger}
                {form.ubyportSubmittedBy === 'AUTO_SWEEP' && t.submittedAutoSweep}
                {form.ubyportSubmittedBy === 'AUTO_RETRY' && t.submittedAutoRetry}
              </div>
            )}
            {form.ubyportError && form.ubyportStatus === 'SENT' && (
              <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px' }}>
                {t.ubyWarnPrefix}{form.ubyportError}
              </div>
            )}
            {form.ubyportError && (form.ubyportStatus === 'SEND_FAILED' || form.ubyportStatus === 'PARTIAL') && (
              <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#c53030' }}>
                {t.ubyDataErrorPrefix}{form.ubyportError}
              </div>
            )}
            {!form.ubyportError && form.ubyportStatus === 'PARTIAL' && awaitingManualCount > 0 && (
              <div
                role="note"
                data-testid="ubyport-awaiting-manual-note"
                style={{ marginTop: 6, fontSize: '0.85rem', color: '#2c5282', background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, padding: '4px 8px' }}
              >
                {t.ubyAwaitingManualNote(awaitingManualCount)}
              </div>
            )}
            {sentWithUnsentGuests && (
              <div
                role="note"
                data-testid="ubyport-unsent-after-sent-note"
                style={{ marginTop: 6, fontSize: '0.85rem', color: '#2c5282', background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, padding: '4px 8px' }}
              >
                {t.ubyUnsentAfterSentNote(unsentForeignCount)}
              </div>
            )}
            {form.ubyportError && form.ubyportStatus === 'SEND_UNAVAILABLE' && (
              <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px' }}>
                {t.ubyConnectivityPrefix}{form.ubyportError}
              </div>
            )}
            <div style={{ marginTop: isMobile ? 10 : 14, display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
              {form.guestEmail && (
                <button
                  onClick={handleResendEmail}
                  disabled={resendingEmail}
                  title={`${t.resendEmailBtn} ${form.guestEmail}`}
                  style={{
                    padding: btnPad,
                    background: resendingEmail ? '#bee3f8' : '#ebf8ff',
                    color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6,
                    cursor: resendingEmail ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: btnFs
                  }}
                >
                  {resendingEmail ? t.resendingEmail : t.resendEmailBtn}
                </button>
              )}
              <button
                onClick={handleResetAll}
                disabled={resettingAll}
                title={t.resetAllBtn}
                style={{
                  padding: btnPad,
                  background: resettingAll ? '#fed7d7' : '#fff5f5',
                  color: '#c53030', border: '1px solid #fed7d7', borderRadius: 6,
                  cursor: resettingAll ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: btnFs
                }}
              >
                {resettingAll ? t.resettingAll : t.resetAllBtn}
              </button>
              <button
                onClick={handleSubmitAll}
                disabled={!canSubmit || submittingAll}
                title={
                  isFutureCheckin ? t.tooltipFutureCheckin
                  : !allSlotsComplete ? t.tooltipSlotsIncomplete
                  : form?.ubyportStatus === 'SENT' && !sentWithUnsentGuests ? t.tooltipAlreadySent
                  : ''
                }
                style={{
                  padding: btnPad,
                  background: submittingAll ? '#bee3f8' : canSubmit ? '#2b6cb0' : '#a0aec0',
                  color: '#fff', border: 'none', borderRadius: 6,
                  cursor: canSubmit && !submittingAll ? 'pointer' : 'not-allowed',
                  fontWeight: 600, fontSize: btnFs
                }}
              >
                {submittingAll ? t.submittingAll : t.submitToUbyport}
              </button>
              {isFutureCheckin && (
                <span style={{ fontSize: '0.82rem', color: '#92400e' }}>
                  {t.futureCheckinNote}
                </span>
              )}
              {!isFutureCheckin && !allSlotsComplete && (form?.members.length ?? 0) > 0 && (
                <span style={{ fontSize: '0.82rem', color: '#718096' }}>
                  {form!.members.filter(m => !m.isComplete).length} {t.slotsStillIncomplete}
                </span>
              )}
            </div>

            {/* Apartment check-in link shown after Reset All */}
            {resetResult && form?.apartmentPublicKey && (
              <div style={{ marginTop: 16, padding: '14px 18px', background: '#f0fff4', border: '1.5px solid #9ae6b4', borderRadius: 8 }}>
                <div style={{ fontWeight: 700, color: '#276749', marginBottom: 6 }}>{t.resetShareLink}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    readOnly
                    value={`${window.location.origin}/check-in/${form.apartmentPublicKey}`}
                    style={{ flex: 1, minWidth: 0, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.85rem', fontFamily: 'monospace', background: '#fff' }}
                    onFocus={e => e.currentTarget.select()}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/check-in/${form.apartmentPublicKey}`);
                    }}
                    style={{ padding: '7px 16px', background: '#38a169', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {t.copyLink}
                  </button>
                </div>
              </div>
            )}
          </div>

          {dorucenky.length === 1 && (
            <div style={{ marginBottom: 24, padding: '14px 18px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontWeight: 700, color: '#276749', fontSize: '0.95rem' }}>{t.dorucenkaIssued}</span>
                <span style={{ marginLeft: 12, fontSize: '0.82rem', color: '#4a5568' }}>
                  {new Date(dorucenky[0].uploadedAt).toLocaleString()}
                </span>
                {dorucenky[0].pseudoRazitko && (
                  <span style={{ marginLeft: 10, fontSize: '0.75rem', color: '#718096' }}>
                    {t.stampLabel}{dorucenky[0].pseudoRazitko}
                  </span>
                )}
              </div>
              <button
                onClick={() => downloadDorucenka(reservationId, dorucenky[0].id)}
                style={{ padding: '6px 16px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap' }}
              >
                {t.downloadDorucenkaBtn}
              </button>
            </div>
          )}

          {dorucenky.length > 1 && (
            <div style={{ marginBottom: 24, padding: '14px 18px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: '#276749', fontSize: '0.95rem' }}>
                  {dorucenky.length}{t.dorucenkasIssuedSuffix}
                </span>
                <button
                  onClick={() => downloadAllDorucenky(reservationId)}
                  style={{ padding: '6px 16px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap' }}
                >
                  {t.downloadAllBtn}
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...dorucenky].sort((a, b) => a.id - b.id).map((doc, i) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: '0.85rem', color: '#4a5568' }}>
                    <span>
                      #{i + 1} — {new Date(doc.uploadedAt).toLocaleString()}
                      {doc.pseudoRazitko && <span style={{ marginLeft: 8, color: '#718096', fontSize: '0.78rem' }}>{t.stampLabel}{doc.pseudoRazitko}</span>}
                    </span>
                    <button
                      onClick={() => downloadDorucenka(reservationId, doc.id)}
                      style={{ padding: '3px 12px', background: 'none', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                    >
                      {t.downloadBtn}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.members.map(slot => (
            <SlotCard
              key={slot.slotIndex}
              slot={slot}
              otherGuestFiled={anyGuestFiled}
              states={states}
              purposes={purposes}
              isEditing={editingSlot === slot.slotIndex}
              saving={saving}
              isMobile={isMobile}
              t={t}
              onEdit={() => setEditingSlot(slot.slotIndex)}
              onCancelEdit={() => setEditingSlot(null)}
              onSave={(data) => handleSave(slot.slotIndex, data)}
              onReset={() => handleReset(slot.slotIndex, [slot.firstName, slot.surname].filter(Boolean).join(' ') || undefined)}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default ReservationCheckinForm;
